import type { Milestone, OnboardingItem, ProjectTask, TaskDependencyEdge } from '../types';
import { stripTime } from './taskSchedule';
import {
  getDependencyEdges,
  dependencyPayload,
} from './taskDependencies';
import {
  isOnboardingBridgeTaskId,
  onboardingTaskIdToItemId,
} from './onboardingTaskBridge';
import { isMilestoneBridgeId, parseMilestoneBridgeId } from './milestoneSchedule';

/** Max snapshots kept per open project tab (older drops off). */
export const TIMELINE_UNDO_MAX_STACK = 40;

function cloneEdges(edges: TaskDependencyEdge[]): TaskDependencyEdge[] {
  return edges.map((e) => ({
    predecessorId: e.predecessorId,
    type: e.type,
    lagBusinessDays: Math.max(-500, Math.min(500, Math.floor(Number(e.lagBusinessDays) || 0))),
  }));
}

export type TimelineUndoTaskPatch = {
  kind: 'task';
  taskId: string;
  startMs: number;
  endMs: number;
  dependencyEdges: TaskDependencyEdge[];
};

export type TimelineUndoMilestonePatch = {
  kind: 'milestone';
  milestoneDocId: string;
  targetMs: number;
  dependencyEdges: TaskDependencyEdge[];
};

export type TimelineUndoOnboardingPatch = {
  kind: 'onboarding';
  /** Synthetic Gantt id (`onboarding-task:…`) for `ganttDateOverrides`. */
  bridgeTaskId: string;
  itemId: string;
  startMs: number;
  endMs: number;
  dependencyEdges: TaskDependencyEdge[];
};

export type TimelineUndoPatch = TimelineUndoTaskPatch | TimelineUndoMilestonePatch | TimelineUndoOnboardingPatch;

/** One undo step: restores all listed entities to baseline captured immediately before their last timeline write. */
export type TimelineUndoSnapshot = {
  projectId: string;
  patches: TimelineUndoPatch[];
};

/**
 * Build a revert snapshot from the Firestore-aligned scheduling baseline (no `ganttDateOverrides`).
 * Caller should capture synchronously — before awaits — using a ref pinned to latest baseline.
 */
export function captureTimelineUndoSnapshot(
  baselineNodes: ProjectTask[],
  projectId: string | null | undefined,
  touchedIds: Iterable<string>,
): TimelineUndoSnapshot | null {
  if (!projectId) return null;
  const byId = new Map<string, ProjectTask>(baselineNodes.filter((t) => t.id).map((t) => [t.id!, t]));
  const patches: TimelineUndoPatch[] = [];
  const seen = new Set<string>();

  for (const id of Array.from(touchedIds)) {
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const n = byId.get(id);
    if (!n?.id) continue;

    const edges = cloneEdges(getDependencyEdges(n));
    const s = stripTime(new Date(n.startDate)).getTime();
    const e = stripTime(new Date(n.endDate)).getTime();

    if (isOnboardingBridgeTaskId(id)) {
      patches.push({
        kind: 'onboarding',
        bridgeTaskId: id,
        itemId: onboardingTaskIdToItemId(id),
        startMs: s,
        endMs: e,
        dependencyEdges: edges,
      });
    } else if (isMilestoneBridgeId(id)) {
      patches.push({
        kind: 'milestone',
        milestoneDocId: parseMilestoneBridgeId(id),
        targetMs: s,
        dependencyEdges: edges,
      });
    } else {
      patches.push({
        kind: 'task',
        taskId: id,
        startMs: s,
        endMs: e,
        dependencyEdges: edges,
      });
    }
  }

  return patches.length ? { projectId, patches } : null;
}

export async function restoreTimelineUndoSnapshot(
  snapshot: TimelineUndoSnapshot,
  ctx: {
    projectId: string;
    updateProjectTask: (id: string, patch: Partial<ProjectTask>) => Promise<void> | void;
    updateMilestone: (milestoneDocId: string, patch: Partial<Milestone>) => Promise<void> | void;
    updateOnboardingItem: (
      projId: string,
      itemId: string,
      patch: Partial<OnboardingItem>,
      actorName: string,
    ) => Promise<void> | void;
    actorName: string;
  },
): Promise<void> {
  if (snapshot.projectId !== ctx.projectId) return;
  for (const p of snapshot.patches) {
    switch (p.kind) {
      case 'task':
        await ctx.updateProjectTask(p.taskId, {
          startDate: new Date(p.startMs),
          endDate: new Date(p.endMs),
          ...dependencyPayload(p.dependencyEdges),
        });
        break;
      case 'milestone':
        await ctx.updateMilestone(p.milestoneDocId, {
          targetDate: new Date(p.targetMs),
          dependencyEdges: p.dependencyEdges,
        });
        break;
      case 'onboarding':
        await ctx.updateOnboardingItem(ctx.projectId, p.itemId, {
          startDate: new Date(p.startMs),
          endDate: new Date(p.endMs),
          dependencyEdges: p.dependencyEdges,
        }, ctx.actorName);
        break;
      default:
        break;
    }
  }
}
